import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserRewards, claimReward } from '../lib/premiosService';
import { useAuth } from '../context/AuthContext';
import '../styles/premios.css';

const MisPremios = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, login } = useAuth();
  
  const [activeTab, setActiveTab] = useState('pendientes');
  const [rewards, setRewards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPagoMovilForm, setShowPagoMovilForm] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);
  
  // Datos del formulario de retiro (campos por método)
  const [pagoForm, setPagoForm] = useState({
    withdrawal_method: 'pago_movil',  // 'pago_movil' | 'bancolombia' | 'nequi'
    // Pago Móvil
    banco: '',
    telefono: '',
    // Bancolombia
    numero_cuenta: '',
    tipo_cuenta: 'ahorros',  // 'ahorros' | 'corriente'
    // Nequi
    numero_nequi: '',
    // Comunes
    cedula: '',
    nombre_titular: '',
    nota: ''
  });
  
  // Toast notifications
  const [toast, setToast] = useState(null);

  // Cargar premios desde el backend API
  const loadRewards = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!user?.uid) {
      if (!silent) setIsLoading(false);
      setRewards([]);
      return;
    }

    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    
    try {
      const backendRewards = await getUserRewards(user.uid);
      
      // El backend ya formatea los datos, mapear directamente
      const formatted = backendRewards.map(r => ({
        id: r.id || '',
        type: r.type || '',
        title: r.title || r.displayName || '',
        displayName: r.displayName || r.title || '',
        amount: r.amount || 0,
        quantity: r.quantity || 0,
        xp: r.xp || 0,
        xpAdded: r.xpAdded || 0,
        movement_xp: r.movement_xp || 0,
        pointsAwarded: r.pointsAwarded || 0,
        autoApplied: !!r.autoApplied,
        pointsBefore: r.pointsBefore || 0,
        pointsAfter: r.pointsAfter || 0,
        status: r.status || 'pending',
        claimable: r.claimable !== false,
        benefit: r.benefit !== false,
        source: r.source || 'ruleta',
        segment_id: r.segment_id || '',
        spin_id: r.spin_id || '',
        prize_id: r.prize_id || r.id || '',
        beneficio_id: r.beneficio_id || '',
        tipo_beneficio: r.tipo_beneficio || '',
        codigo_asignado: r.codigo_asignado === true,
        codigo_descuento: r.codigo_descuento || '',
        porcentaje_descuento: r.porcentaje_descuento,
        fecha_asignacion: r.fecha_asignacion || '',
        fecha_vencimiento: r.fecha_vencimiento || '',
        estado_beneficio: r.estado_beneficio || '',
        user_message: r.user_message || '',
        correction_message: r.correction_message || '',
        claim_data: r.claim_data || null,
        wonAt: r.wonAt || '',
        claimedAt: r.claimedAt || '',
        meta: r.meta || {}
      }));
      
      setRewards(formatted);
      if (silent) setError(null); // si la carga silenciosa funciona, limpia errores previos
    } catch (err) {
      console.error('Error loading rewards:', err);
      if (!silent) setError('network');
      setRewards([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!authLoading) {
      loadRewards();
    }
  }, [loadRewards, authLoading]);

  // ===== Polling ligero (12s) mientras la pantalla esté visible.
  // Refresco silencioso: NO toggle del spinner, NO sobre-escribe error si falla.
  // Refresca también al recibir focus / visibilitychange.
  const rewardsPollInFlight = useRef(false);
  useEffect(() => {
    if (authLoading || !user?.uid) return undefined;

    const tick = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (rewardsPollInFlight.current) return;
      rewardsPollInFlight.current = true;
      try {
        await loadRewards({ silent: true });
      } finally {
        rewardsPollInFlight.current = false;
      }
    };

    const interval = setInterval(tick, 12000);
    const onFocus = () => tick();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [authLoading, user?.uid, loadRewards]);

  // Mostrar toast (acepta extras opcionales como { visualKey, xpAdded } para animación XP)
  const showToast = (message, type = 'success', extras = null) => {
    setToast({ message, type, ...(extras || {}) });
    setTimeout(() => setToast(null), extras?.visualKey ? 3600 : 3000);
  };

  // Formatear fecha
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const month = months[date.getMonth()];
    const hours = date.getHours();
    const mins = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    return `${day} ${month} · ${hour12}:${mins} ${ampm}`;
  };

  const isDeliveryDiscount = (reward) => (
    reward?.beneficio_id === 'descuento_envio' ||
    reward?.tipo_beneficio === 'descuento_envio' ||
    Boolean(String(reward?.codigo_descuento || '').trim())
  );

  const formatDiscountExpiry = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('es-CO', {
      day: 'numeric', month: 'long', year: 'numeric'
    }).format(date);
  };

  const isDiscountExpired = (reward) => {
    const expiresAt = new Date(reward?.fecha_vencimiento || '').getTime();
    return Number.isFinite(expiresAt) && expiresAt <= Date.now();
  };

  const copyDiscountCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      showToast('Código copiado', 'success');
    } catch {
      showToast('No pudimos copiar el código', 'error');
    }
  };

  // Abrir modal según tipo de premio
  const openConfirmModal = (reward) => {
    setSelectedReward(reward);
    // Un descuento combinado conserva type=xp, pero su reclamo solo revela
    // el codigo ya reservado; nunca debe pasar por una experiencia de XP.
    if (isDeliveryDiscount(reward)) {
      autoDeliverReward(reward);
      return;
    }
    // SOLO premios de dinero (money_cop) muestran el formulario de Pago Móvil.
    // Cualquier otro tipo (ticket, xp, extra_spin, temporal) se auto-entrega.
    if (reward.type === 'money_cop') {
      // Precargar datos anteriores si existen (ej: corrección)
      if (reward.claim_data) {
        setPagoForm({
          withdrawal_method: reward.claim_data.withdrawal_method || 'pago_movil',
          banco: reward.claim_data.banco || '',
          telefono: reward.claim_data.telefono || '',
          numero_cuenta: reward.claim_data.numero_cuenta || '',
          tipo_cuenta: reward.claim_data.tipo_cuenta || 'ahorros',
          numero_nequi: reward.claim_data.numero_nequi || '',
          cedula: reward.claim_data.cedula || '',
          nombre_titular: reward.claim_data.nombre_titular || '',
          nota: reward.claim_data.nota || ''
        });
      } else {
        setPagoForm({
          withdrawal_method: 'pago_movil',
          banco: '', telefono: '',
          numero_cuenta: '', tipo_cuenta: 'ahorros',
          numero_nequi: '',
          cedula: '', nombre_titular: '', nota: ''
        });
      }
      setShowPagoMovilForm(true);
    } else {
      // Beneficios (tickets, XP, giro adicional, temporal): auto-entregar sin formulario
      autoDeliverReward(reward);
    }
  };

  // Auto-entregar premios que no son dinero (ticket, xp, extra_spin, temporal)
  const autoDeliverReward = async (reward) => {
    if (isClaiming || !user?.uid) return;
    setIsClaiming(true);
    try {
      const result = await claimReward(reward.id, user.uid);
      if (!result) throw new Error('Error al entregar');
      if (isDeliveryDiscount(reward)) {
        setRewards((current) => current.map((item) => (
          item.id === reward.id
            ? {
                ...item,
                status: result.newStatus || 'delivered',
                claimable: false,
                estado_beneficio: 'reclamado',
                codigo_descuento: result.codigo_descuento || '',
              }
            : item
        )));
        showToast(result.message || 'Descuento reclamado', 'success');
        loadRewards({ silent: true });
        return;
      }
      // Caso especial: si el premio es "Giro adicional", redirigir a la
      // ruleta y disparar el giro automáticamente (sin toast intermedio).
      const rt = (reward.type || '').toLowerCase();
      const isExtraSpin = rt === 'extra_spin' || rt === 'free_spin' || rt === 'giro';
      if (isExtraSpin) {
        navigate('/', { state: { autoSpin: true } });
        return;
      }
      // Mensaje dinámico basado en la respuesta del backend (no hardcode)
      const msg = result.message || 'Premio aplicado';
      if (result.rewardType === 'xp' && result.visualKey) {
        showToast(msg, 'xp', { visualKey: result.visualKey, xpAdded: result.xpAdded });
      } else {
        showToast(msg, 'success');
      }
      loadRewards();
    } catch (err) {
      console.error('Error auto-delivering:', err);
      showToast('Error al aplicar el premio. Intenta otra vez.', 'error');
    } finally {
      setIsClaiming(false);
    }
  };

  // Enviar formulario de retiro para premios de dinero
  const submitPagoMovil = async () => {
    if (!selectedReward || isClaiming || !user?.uid) return;

    const method = pagoForm.withdrawal_method || 'pago_movil';

    // Validación por método
    if (method === 'pago_movil') {
      if (!pagoForm.banco || !pagoForm.telefono || !pagoForm.cedula || !pagoForm.nombre_titular) {
        showToast('Completa todos los campos obligatorios', 'error');
        return;
      }
    } else if (method === 'bancolombia') {
      if (!pagoForm.nombre_titular || !pagoForm.numero_cuenta || !pagoForm.tipo_cuenta || !pagoForm.cedula) {
        showToast('Completa todos los campos obligatorios', 'error');
        return;
      }
    } else if (method === 'nequi') {
      if (!pagoForm.nombre_titular || !pagoForm.numero_nequi || !pagoForm.cedula) {
        showToast('Completa todos los campos obligatorios', 'error');
        return;
      }
    }

    // Armar claimData solo con campos del método elegido
    const claimData = {
      withdrawal_method: method,
      nombre_titular: pagoForm.nombre_titular,
      cedula: pagoForm.cedula,
      nota: pagoForm.nota,
    };
    if (method === 'pago_movil') {
      claimData.banco = pagoForm.banco;
      claimData.telefono = pagoForm.telefono;
    } else if (method === 'bancolombia') {
      claimData.numero_cuenta = pagoForm.numero_cuenta;
      claimData.tipo_cuenta = pagoForm.tipo_cuenta;
    } else if (method === 'nequi') {
      claimData.numero_nequi = pagoForm.numero_nequi;
    }

    setIsClaiming(true);
    try {
      const success = await claimReward(selectedReward.id, user.uid, claimData);

      if (!success) throw new Error('Error al reclamar');

      showToast('Solicitud de pago enviada', 'success');
      setShowPagoMovilForm(false);
      setSelectedReward(null);
      loadRewards();
    } catch (err) {
      console.error('Error submitting retiro:', err);
      showToast('Error al enviar. Intenta otra vez.', 'error');
    } finally {
      setIsClaiming(false);
    }
  };

  // Confirmar reclamo genérico (legacy fallback)
  const confirmClaim = async () => {
    if (!selectedReward || isClaiming || !user?.uid) return;
    
    setIsClaiming(true);
    
    try {
      const success = await claimReward(selectedReward.id, user.uid);
      
      if (!success) throw new Error('Error al reclamar');
      
      showToast('Listo. Premio reclamado', 'success');
      setShowConfirmModal(false);
      setSelectedReward(null);
      loadRewards();
    } catch (err) {
      console.error('Error claiming reward:', err);
      showToast('Ups. No pudimos reclamarlo. Intenta otra vez.', 'error');
    } finally {
      setIsClaiming(false);
    }
  };

  // Obtener nombre del premio
  // El backend ya envía `title` correctamente formateado según tipo y cantidad.
  // No reconstruimos: evitamos que "2 Tiquetes" aparezca como "1 Ticket" o "0".
  // Cantidad de XP ganada — prioridad estricta usando ?? (jamás quantity/valor/amount/unidades)
  const getXpGanada = (reward) => {
    return reward.xpAdded
      ?? reward.movement_xp
      ?? reward.pointsAwarded
      ?? reward.xp
      ?? 0;
  };

  const getRewardName = (reward) => {
    // XP type: SIEMPRE usar el cálculo dinámico (ignora title legacy que pueda decir "+10 XP")
    if (reward.type === 'xp') {
      const amt = getXpGanada(reward);
      return amt > 0 ? `+${amt} XP` : 'XP acumulada';
    }
    if (reward.title && reward.title !== '0') return reward.title;
    if (reward.displayName && reward.displayName !== '0') return reward.displayName;
    // Fallback por tipo si no hay title válido
    if (reward.type === 'money_cop') {
      return `$${(reward.amount || 0).toLocaleString('es-CO')} COP`;
    }
    if (reward.type === 'ticket') {
      const q = reward.quantity || 1;
      return `${q} Tiquete${q > 1 ? 's' : ''}`;
    }
    if (reward.type === 'extra_spin') return 'Giro adicional';
    if (reward.type === 'temporal') return 'Premio temporal';
    if (reward.type === 'message') return reward.title || 'Sigue intentando';
    return 'Premio';
  };

  // Obtener ícono del premio
  const getRewardIcon = (reward) => {
    const type = (reward.type || 'gift').toLowerCase();
    const cantidad = parseInt(reward.cantidad || reward.tickets || 0, 10) || 0;

    if (type === 'cash' || type === 'jackpot' || type === 'money' || type === 'money_cop') return 'money';
    if (type === 'ticket') {
      if (reward.ticketType === 'golden') return 'golden';
      return cantidad >= 2 ? 'ticket-double' : 'ticket';
    }
    if (type === 'free_spin' || type === 'extra_spin' || type === 'giro') return 'spin';
    if (type === 'xp_multiplier' || type === 'multiplier') return 'xp-multi';
    if (type === 'xp') return 'xp';
    if (type === 'message' || type === 'mensaje') return 'message';
    if (type === 'temp_prize' || type === 'temporal') return 'gift';
    return 'gift';
  };

  // Filtrar premios por pestaña
  // Disponibles → available (incluye money_cop available que muestra "Reclamar" con Pago Móvil)
  //             + needs_fix / correction_required
  // Pendientes → money_cop pending (ya con Pago Móvil enviado, espera admin)
  // Entregados → delivered | approved | applied (mensajes) | claimed (legacy non-money)
  const getFilteredRewards = () => {
    switch (activeTab) {
      case 'pendientes':
        return rewards.filter(r => 
          r.status === 'available' || 
          r.status === 'needs_fix' || 
          r.status === 'correction_required'
        );
      case 'proceso':
        return rewards.filter(r => 
          (r.status === 'pending' && r.type === 'money_cop') ||
          (r.status === 'claimed' && r.type === 'money_cop')
        );
      case 'historial':
        return rewards.filter(r => 
          r.status === 'delivered' || 
          r.status === 'approved' ||
          r.status === 'applied' ||
          (r.status === 'claimed' && r.type !== 'money_cop')
        );
      default:
        return [];
    }
  };

  // Obtener badge de estado
  const getStatusBadge = (status) => {
    const badges = {
      available: { emoji: '🟢', text: 'Disponible', class: 'disponible' },
      pending: { emoji: '🟣', text: 'Pendiente', class: 'en_proceso' },
      needs_fix: { emoji: '🟠', text: 'Requiere acción', class: 'needs_fix' },
      correction_required: { emoji: '🔶', text: 'Requiere corrección', class: 'correction' },
      claimed: { emoji: '🟣', text: 'Pendiente', class: 'en_proceso' },
      delivered: { emoji: '🟢', text: 'Entregado', class: 'reclamado' },
      approved: { emoji: '🟢', text: 'Aprobado', class: 'reclamado' },
      applied: { emoji: '🔵', text: 'Registrado', class: 'reclamado' },
      cancelled: { emoji: '🔴', text: 'Cancelado', class: 'cancelado' }
    };
    return badges[status] || badges.available;
  };

  const filteredRewards = getFilteredRewards();
  const pendingCount = rewards.filter(r => 
    r.status === 'available' ||
    r.status === 'needs_fix' || 
    r.status === 'correction_required'
  ).length;
  const procesoCount = rewards.filter(r => 
    (r.status === 'pending' && r.type === 'money_cop') ||
    (r.status === 'claimed' && r.type === 'money_cop')
  ).length;

  // Renderizar empty state
  const renderEmptyState = () => {
    const emptyStates = {
      pendientes: {
        icon: '🎁',
        title: 'No tienes premios disponibles',
        subtitle: 'Gira la ruleta y cuando ganes, tus premios aparecerán aquí para reclamarlos',
        cta: 'Ir a la ruleta',
        action: () => navigate('/')
      },
      proceso: {
        icon: '⏳',
        title: 'No hay premios en proceso',
        subtitle: 'Cuando un premio esté siendo validado o entregado, lo verás aquí',
        cta: 'Ver historial',
        action: () => setActiveTab('historial')
      },
      historial: {
        icon: '🧾',
        title: 'Aún no tienes historial de premios',
        subtitle: 'Aquí quedará el registro de todos los premios que ganes y reclames',
        cta: 'Ir a la ruleta',
        action: () => navigate('/')
      }
    };

    const state = emptyStates[activeTab];

    return (
      <div className="empty-state">
        <div className="empty-icon">{state.icon}</div>
        <h3 className="empty-title">{state.title}</h3>
        <p className="empty-subtitle">{state.subtitle}</p>
        <button className="empty-cta" onClick={state.action}>
          {state.cta}
        </button>
      </div>
    );
  };

  // Renderizar loading
  const renderLoading = () => (
    <div className="loading-state">
      <div className="loading-spinner"></div>
      <h3 className="loading-title">Cargando tus premios…</h3>
      <p className="loading-subtitle">Un momento, estamos trayendo tu información</p>
    </div>
  );

  // Renderizar error de red
  const renderNetworkError = () => (
    <div className="error-state">
      <div className="error-icon">📡</div>
      <h3 className="error-title">No pudimos cargar tus premios</h3>
      <p className="error-subtitle">Revisa tu internet e inténtalo de nuevo</p>
      <button className="error-retry" onClick={loadRewards}>
        Reintentar
      </button>
    </div>
  );

  // Renderizar tarjeta de premio
  const renderRewardCard = (reward) => {
    const deliveryDiscount = isDeliveryDiscount(reward);
    const discountExpired = deliveryDiscount && isDiscountExpired(reward);
    const discountExpiryTime = new Date(reward.fecha_vencimiento || '').getTime();
    const discountPercentage = Number(reward.porcentaje_descuento);
    const discountComplete = Boolean(
      reward.codigo_asignado === true &&
      Number.isFinite(discountPercentage) && discountPercentage > 0 &&
      Number.isFinite(discountExpiryTime)
    );
    const iconType = deliveryDiscount ? 'gift' : getRewardIcon(reward);
    const statusBadge = discountExpired
      ? { emoji: '⚪', text: 'Vencido', class: 'cancelado' }
      : getStatusBadge(reward.status);
    const isClaimable = reward.status === 'available' && !discountExpired && (!deliveryDiscount || discountComplete);
    const needsFix = reward.status === 'needs_fix';
    const needsCorrection = reward.status === 'correction_required';
    const isInProcess = (reward.status === 'pending' && reward.type === 'money_cop') || (reward.status === 'claimed' && reward.type === 'money_cop');
    const isDelivered = reward.status === 'delivered' || reward.status === 'approved' || reward.status === 'applied' || (reward.status === 'claimed' && reward.type !== 'money_cop');

    // DEBUG XP — eliminar después de identificar la fuente del valor incorrecto
    if (reward.type === 'xp') {
      // eslint-disable-next-line no-console
      console.log("DEBUG XP:", {
        id: reward.id,
        title: reward.title,
        displayName: reward.displayName,
        type: reward.type,
        status: reward.status,
        xpAdded: reward.xpAdded,
        movement_xp: reward.movement_xp,
        pointsAwarded: reward.pointsAwarded,
        xp: reward.xp,
        quantity: reward.quantity,
        amount: reward.amount,
        autoApplied: reward.autoApplied,
        getXpGanada_result: getXpGanada(reward),
        full: reward,
      });
    }

    return (
      <div key={reward.id} className={`reward-card ${reward.type} ${needsFix ? 'needs-fix' : ''}`}>
        {/* Ícono */}
        <div className={`reward-icon ${iconType}`} aria-hidden="true">
          {iconType === 'money' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-money" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FBBF24"/>
                  <stop offset="100%" stopColor="#F59E0B"/>
                </linearGradient>
              </defs>
              <circle cx="24" cy="24" r="18" fill="url(#ri-money)" stroke="#FDE68A" strokeWidth="2.5"/>
              <text x="24" y="31" textAnchor="middle" fontSize="20" fontWeight="900" fill="#7C2D12" fontFamily="system-ui">$</text>
              <circle cx="14" cy="14" r="2" fill="#FDE68A" opacity="0.85"/>
              <circle cx="35" cy="32" r="1.6" fill="#FDE68A" opacity="0.7"/>
            </svg>
          )}
          {iconType === 'golden' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-gold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FDE68A"/>
                  <stop offset="100%" stopColor="#F59E0B"/>
                </linearGradient>
              </defs>
              <path d="M24 6L29.2 18.5L42.5 19.5L32.5 28.5L36 41L24 33.5L12 41L15.5 28.5L5.5 19.5L18.8 18.5L24 6Z" fill="url(#ri-gold)" stroke="#FFFBEB" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          )}
          {iconType === 'ticket' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-ticket" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#A064FF"/>
                  <stop offset="100%" stopColor="#7C3AED"/>
                </linearGradient>
              </defs>
              <path d="M8 16 a4 4 0 014-4 h24 a4 4 0 014 4 v3 a3 3 0 000 6 v3 a4 4 0 01-4 4 h-24 a4 4 0 01-4-4 v-3 a3 3 0 000-6 v-3 z"
                fill="url(#ri-ticket)" stroke="#D8B4FE" strokeWidth="2" strokeLinejoin="round"/>
              <line x1="22" y1="14" x2="22" y2="34" stroke="#1A0F2E" strokeWidth="1.5" strokeDasharray="3 3"/>
              <text x="14" y="28" fontSize="9" fontWeight="800" fill="#FFFFFF">x1</text>
              <circle cx="32" cy="24" r="2.2" fill="#FDE68A" filter="url(#ri-ticket-glow)"/>
            </svg>
          )}
          {iconType === 'ticket-double' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-ticket2" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#A064FF"/>
                  <stop offset="100%" stopColor="#6D28D9"/>
                </linearGradient>
              </defs>
              <path d="M11 12 a3 3 0 013-3 h22 a3 3 0 013 3 v3 a3 3 0 000 6 v3 a3 3 0 01-3 3 h-22 a3 3 0 01-3-3 v-3 a3 3 0 000-6 v-3 z"
                transform="translate(-2 -2)" fill="url(#ri-ticket2)" opacity="0.55" stroke="#D8B4FE" strokeWidth="1.5"/>
              <path d="M11 12 a3 3 0 013-3 h22 a3 3 0 013 3 v3 a3 3 0 000 6 v3 a3 3 0 01-3 3 h-22 a3 3 0 01-3-3 v-3 a3 3 0 000-6 v-3 z"
                transform="translate(4 4)" fill="url(#ri-ticket2)" stroke="#D8B4FE" strokeWidth="2" strokeLinejoin="round"/>
              <text x="20" y="32" fontSize="11" fontWeight="900" fill="#FFFFFF">x2</text>
            </svg>
          )}
          {iconType === 'spin' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-spin" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#22D3EE"/>
                  <stop offset="100%" stopColor="#7C3AED"/>
                </linearGradient>
              </defs>
              <circle cx="24" cy="24" r="14" stroke="url(#ri-spin)" strokeWidth="3.5" fill="none" strokeDasharray="60 18" transform="rotate(-90 24 24)"/>
              <path d="M34 18 L40 14 L36 22 Z" fill="#22D3EE"/>
              <circle cx="24" cy="24" r="4" fill="url(#ri-spin)" stroke="#fff" strokeWidth="1.5"/>
            </svg>
          )}
          {iconType === 'xp' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-xp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#A064FF"/>
                  <stop offset="100%" stopColor="#5B21B6"/>
                </linearGradient>
              </defs>
              <polygon points="24,4 42,14 42,34 24,44 6,34 6,14" fill="url(#ri-xp)" stroke="#D8B4FE" strokeWidth="2.5" strokeLinejoin="round"/>
              <text x="24" y="30" textAnchor="middle" fontSize="13" fontWeight="900" fill="#FFFFFF" letterSpacing="0.5" fontFamily="system-ui">XP</text>
            </svg>
          )}
          {iconType === 'xp-multi' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-xpm" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FBBF24"/>
                  <stop offset="100%" stopColor="#A064FF"/>
                </linearGradient>
              </defs>
              <polygon points="24,4 42,14 42,34 24,44 6,34 6,14" fill="url(#ri-xpm)" stroke="#FDE68A" strokeWidth="2.5" strokeLinejoin="round"/>
              <text x="24" y="22" textAnchor="middle" fontSize="9" fontWeight="800" fill="#FFFFFF" fontFamily="system-ui">XP</text>
              <text x="24" y="36" textAnchor="middle" fontSize="13" fontWeight="900" fill="#FFFBEB" fontFamily="system-ui">x2</text>
            </svg>
          )}
          {iconType === 'message' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-msg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FB923C"/>
                  <stop offset="50%" stopColor="#F472B6"/>
                  <stop offset="100%" stopColor="#A064FF"/>
                </linearGradient>
              </defs>
              <path d="M24 6 C26 14, 34 14, 32 22 C36 22, 38 28, 34 32 C38 36, 32 42, 24 42 C16 42, 10 36, 14 32 C10 28, 12 22, 16 22 C14 14, 22 14, 24 6 Z"
                fill="url(#ri-msg)" stroke="#FFFFFF" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M24 18 C25 23, 28 24, 27 28 C30 30, 28 34, 24 34 C20 34, 18 30, 21 28 C20 24, 23 23, 24 18 Z"
                fill="#FEF3C7" opacity="0.7"/>
            </svg>
          )}
          {iconType === 'gift' && (
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="ri-gift" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FB923C"/>
                  <stop offset="100%" stopColor="#C2410C"/>
                </linearGradient>
              </defs>
              <rect x="8" y="20" width="32" height="22" rx="3" fill="url(#ri-gift)" stroke="#FED7AA" strokeWidth="2"/>
              <rect x="6" y="14" width="36" height="9" rx="2" fill="url(#ri-gift)" stroke="#FED7AA" strokeWidth="2"/>
              <line x1="24" y1="14" x2="24" y2="42" stroke="#FED7AA" strokeWidth="2.5"/>
              <path d="M24 14 C24 14, 18 8, 14 12 C12 14, 16 16, 18 16 C20 16, 24 14, 24 14 Z"
                fill="#FED7AA" stroke="#FED7AA" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M24 14 C24 14, 30 8, 34 12 C36 14, 32 16, 30 16 C28 16, 24 14, 24 14 Z"
                fill="#FED7AA" stroke="#FED7AA" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          )}
        </div>

        {/* Info */}
        <div className="reward-info">
          <h3 className="reward-name">
            {deliveryDiscount
              ? (isDelivered
                ? '🎉 ¡Descuento reclamado!'
                : (Number.isFinite(discountPercentage) && discountPercentage > 0
                  ? `🎁 ${reward.porcentaje_descuento}% de descuento en tu próximo envío`
                  : '🎁 Premio de descuento'))
              : (reward.type === 'xp' ? 'XP acumulada' : getRewardName(reward))}
          </h3>
          {deliveryDiscount ? (
            <div className="delivery-benefit-details">
              <p>De Colombia a Venezuela</p>
              {isDelivered ? (
                <>
                  <p>Tu premio está listo para usar en Giro Exprés.</p>
                  <p>Descuento: <strong>{reward.porcentaje_descuento}%</strong></p>
                  <p className="delivery-benefit-code">Código: <strong>{reward.codigo_descuento}</strong></p>
                </>
              ) : (
                <p>Reclama tu premio y descubre el código que podrás utilizar en Giro Exprés.</p>
              )}
              <p>Válido hasta {formatDiscountExpiry(reward.fecha_vencimiento) || 'fecha no disponible'}</p>
              {!discountComplete && (
                <p className="delivery-benefit-error">El premio está incompleto y no puede reclamarse.</p>
              )}
            </div>
          ) : (
            <p className="reward-date">Ganado: {formatDate(reward.wonAt)}</p>
          )}
          {!deliveryDiscount && reward.type === 'xp' && getXpGanada(reward) > 0 && (
            <p className="reward-xp-earned" data-testid="reward-xp-earned">
              XP ganada: <span className="reward-xp-earned-amount">+{getXpGanada(reward)} XP</span>
            </p>
          )}
          <p className="reward-status">
            Estado: <span className={`status-text ${statusBadge.class}`}>{statusBadge.text}</span>
          </p>
          {/* Mostrar mensaje de corrección: needs_fix lee de claim_data.fix_user_message */}
          {needsFix && (
            <p className="reward-correction-message" data-testid="correction-message">
              {reward.claim_data?.fix_user_message 
                ? `📝 ${reward.claim_data.fix_user_message}` 
                : '📝 Se requiere corregir los datos de pago.'}
            </p>
          )}
          {/* Mostrar mensaje de corrección: correction_required lee de correction_message */}
          {needsCorrection && (
            <p className="reward-correction-message" data-testid="correction-message">
              {reward.correction_message 
                ? `📝 ${reward.correction_message}` 
                : '📝 Se requiere corregir los datos de pago. Revisa tu información.'}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="reward-actions">
          <span className={`status-badge ${statusBadge.class}`}>
            {statusBadge.emoji} {statusBadge.text}
          </span>
          
          {isClaimable && (
            <button 
              className="btn-reclamar"
              onClick={() => openConfirmModal(reward)}
            >
              Reclamar premio
            </button>
          )}

          {needsCorrection && (
            <button 
              className="btn-corregir"
              onClick={() => openConfirmModal(reward)}
              data-testid="btn-corregir"
            >
              Corregir datos
            </button>
          )}
          
          {needsFix && (
            <button 
              className="btn-corregir"
              onClick={() => openConfirmModal(reward)}
              data-testid="btn-corregir"
            >
              Corregir datos
            </button>
          )}
          
          {isInProcess && (
            <span className="status-info-text">Procesando pago</span>
          )}
          
          {isDelivered && (
            deliveryDiscount ? (
              <button
                type="button"
                className="btn-copy-discount"
                onClick={() => copyDiscountCode(reward.codigo_descuento)}
              >
                Copiar código
              </button>
            ) : <span className="delivered-check">✅</span>
          )}
        </div>
      </div>
    );
  };

  // Renderizar mensaje de login necesario
  const renderLoginRequired = () => (
    <div className="login-required-state">
      <div className="login-icon">🔐</div>
      <h3 className="login-title">Inicia sesión para ver tus premios</h3>
      <p className="login-subtitle">Necesitas una cuenta para guardar y reclamar premios</p>
      <button className="login-cta" onClick={login}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Iniciar con Google
      </button>
    </div>
  );

  return (
    <div className="premios-container">
      {/* Header */}
      <header className="premios-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="header-title">Mis Premios</h1>
        <div className="header-spacer"></div>
      </header>

      {/* Mostrar login si no está autenticado */}
      {!authLoading && !isAuthenticated ? (
        <div className="premios-content">
          {renderLoginRequired()}
        </div>
      ) : (
        <>
          {/* Subtítulo entre header y resumen */}
          <p className="premios-subtitle" data-testid="premios-subtitle">
            Tus recompensas disponibles y pendientes
          </p>

          {/* Resumen de premios disponibles - neón gaming */}
          {pendingCount > 0 && (
            <div className="premios-summary-card" data-testid="premios-summary-card">
              <svg className="premios-summary-icon" viewBox="0 0 44 44" fill="none" aria-hidden="true">
                <rect x="6" y="18" width="32" height="20" rx="3" stroke="currentColor" strokeWidth="3" fill="none"/>
                <rect x="10" y="10" width="24" height="10" rx="2" stroke="currentColor" strokeWidth="3" fill="none"/>
                <line x1="22" y1="10" x2="22" y2="38" stroke="currentColor" strokeWidth="3"/>
                <path d="M22 10C22 10 18 6 14 8C10 10 12 14 14 16C16 18 22 18 22 18" stroke="currentColor" strokeWidth="2.5" fill="none"/>
                <path d="M22 10C22 10 26 6 30 8C34 10 32 14 30 16C28 18 22 18 22 18" stroke="currentColor" strokeWidth="2.5" fill="none"/>
              </svg>
              <span className="premios-summary-count">{pendingCount}</span>
              <span className="premios-summary-label">premios disponibles</span>
              {/* Partículas alrededor del borde */}
              <span className="premios-particle pp1" aria-hidden="true"></span>
              <span className="premios-particle pp2" aria-hidden="true"></span>
              <span className="premios-particle pp3" aria-hidden="true"></span>
              <span className="premios-particle pp4" aria-hidden="true"></span>
              <span className="premios-particle pp5 amber" aria-hidden="true"></span>
              <span className="premios-particle pp6 amber" aria-hidden="true"></span>
              <span className="premios-particle pp7" aria-hidden="true"></span>
              <span className="premios-particle pp8" aria-hidden="true"></span>
              <span className="premios-particle pp9" aria-hidden="true"></span>
              <span className="premios-particle pp10 amber" aria-hidden="true"></span>
              <span className="premios-particle pp11" aria-hidden="true"></span>
              <span className="premios-particle pp12" aria-hidden="true"></span>
              <span className="premios-particle pp13 blue" aria-hidden="true"></span>
              <span className="premios-particle pp14 blue" aria-hidden="true"></span>
              <span className="premios-particle pp15 blue" aria-hidden="true"></span>
              <span className="premios-particle pp16 blue" aria-hidden="true"></span>
            </div>
          )}

          {/* Tabs - Renombrado: Disponibles → Pendientes */}
          <div className="tabs-container">
            <button 
              className={`tab ${activeTab === 'pendientes' ? 'active' : ''}`}
              onClick={() => setActiveTab('pendientes')}
            >
              <svg className="tab-icon-gift" width="28" height="28" viewBox="0 0 44 44" fill="none" aria-hidden="true">
                <rect x="6" y="18" width="32" height="20" rx="3" stroke="currentColor" strokeWidth="3" fill="none"/>
                <rect x="10" y="10" width="24" height="10" rx="2" stroke="currentColor" strokeWidth="3" fill="none"/>
                <line x1="22" y1="10" x2="22" y2="38" stroke="currentColor" strokeWidth="3"/>
                <path d="M22 10C22 10 18 6 14 8C10 10 12 14 14 16C16 18 22 18 22 18" stroke="currentColor" strokeWidth="2.5" fill="none"/>
                <path d="M22 10C22 10 26 6 30 8C34 10 32 14 30 16C28 18 22 18 22 18" stroke="currentColor" strokeWidth="2.5" fill="none"/>
              </svg>
              Disponibles {pendingCount > 0 && <span className="tab-badge">{pendingCount}</span>}
            </button>
            <button 
              className={`tab ${activeTab === 'proceso' ? 'active' : ''}`}
              onClick={() => setActiveTab('proceso')}
            >
              <svg className="tab-icon-clock" width="26" height="26" viewBox="0 0 44 44" fill="none" aria-hidden="true">
                <circle cx="22" cy="24" r="14" stroke="currentColor" strokeWidth="3" fill="none"/>
                <path d="M22 16 V24 L28 28" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M16 6 L11 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                <path d="M28 6 L33 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
              Pendientes {procesoCount > 0 && <span className="tab-badge proceso">{procesoCount}</span>}
            </button>
            <button 
              className={`tab ${activeTab === 'historial' ? 'active' : ''}`}
              onClick={() => setActiveTab('historial')}
            >
              <svg className="tab-icon-check" width="30" height="30" viewBox="0 0 44 44" fill="none" aria-hidden="true">
                <rect x="9" y="6" width="26" height="32" rx="3" stroke="currentColor" strokeWidth="3" fill="none"/>
                <path d="M14 22 L20 28 L31 16" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              Entregados
            </button>
          </div>

          {/* Content */}
          <div className="premios-content">
            {/* Loading */}
            {(isLoading || authLoading) && renderLoading()}
            
            {/* Error */}
            {!isLoading && !authLoading && error === 'network' && renderNetworkError()}
            
            {/* Content */}
            {!isLoading && !authLoading && !error && (
              <>
                {/* Lista o empty state */}
                {filteredRewards.length === 0 ? (
                  renderEmptyState()
                ) : (
                  <div className="rewards-list">
                    {filteredRewards.map(renderRewardCard)}
                  </div>
                )}
                
                {/* Info de soporte para En proceso */}
                {activeTab === 'proceso' && filteredRewards.length > 0 && (
                  <div className="proceso-info">
                    <p>Estamos gestionando tus premios. Si necesitas soporte, escríbenos y te ayudamos.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Footer */}
      <footer className="premios-footer">
        {/* Hint gaming "Los premios se actualizan en tiempo real" */}
        <div className="premios-realtime-hint" aria-hidden="true">
          <span className="premios-hint-line left"></span>
          <span className="premios-hint-diamond"></span>
          <span className="premios-hint-text">Los premios se actualizan en tiempo real</span>
          <span className="premios-hint-diamond"></span>
          <span className="premios-hint-line right"></span>
        </div>
      </footer>

      {/* Modal de Confirmación (fallback) */}
      {showConfirmModal && selectedReward && (
        <div className="modal-overlay" onClick={() => !isClaiming && setShowConfirmModal(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Confirmar reclamo</h3>
            <p className="modal-text">
              ¿Deseas reclamar este premio ahora?
            </p>
            <div className="modal-reward">
              <strong>{getRewardName(selectedReward)}</strong>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => setShowConfirmModal(false)}
                disabled={isClaiming}
              >
                Cancelar
              </button>
              <button 
                className="btn-confirm"
                onClick={confirmClaim}
                disabled={isClaiming}
              >
                {isClaiming ? 'Reclamando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reclamo - Datos para recibir el premio (solo dinero) */}
      {showPagoMovilForm && selectedReward && (
        <div className="modal-overlay" onClick={() => !isClaiming && setShowPagoMovilForm(false)}>
          <div className="pago-movil-modal" onClick={e => e.stopPropagation()} data-testid="pago-movil-modal">
            <h3 className="modal-title">Datos para recibir tu premio</h3>
            <p className="modal-helper-text">
              Puedes recibir tu premio por Pago Móvil, Bancolombia o Nequi.
            </p>
            <p className="modal-subtitle">Para recibir <strong>{getRewardName(selectedReward)}</strong></p>

            <div className="pago-form">
              {/* Selector: método de retiro */}
              <div className="form-field">
                <label>ELIGE CÓMO QUIERES RECIBIR TU PREMIO *</label>
                <select
                  value={pagoForm.withdrawal_method}
                  onChange={e => setPagoForm(p => ({ ...p, withdrawal_method: e.target.value }))}
                  data-testid="pago-metodo"
                >
                  <option value="pago_movil">Pago Móvil</option>
                  <option value="bancolombia">Bancolombia</option>
                  <option value="nequi">Nequi</option>
                </select>
              </div>

              {/* ---------- PAGO MÓVIL ---------- */}
              {pagoForm.withdrawal_method === 'pago_movil' && (
                <>
                  <div className="form-field">
                    <label>Banco *</label>
                    <select
                      value={pagoForm.banco}
                      onChange={e => setPagoForm(p => ({...p, banco: e.target.value}))}
                      data-testid="pago-banco"
                    >
                      <option value="">Seleccionar banco</option>
                      <option value="0102">Banco de Venezuela</option>
                      <option value="0104">Venezolano de Crédito</option>
                      <option value="0105">Mercantil</option>
                      <option value="0108">BBVA Provincial</option>
                      <option value="0114">Bancaribe</option>
                      <option value="0115">Banco Exterior</option>
                      <option value="0128">Banco Caroní</option>
                      <option value="0134">Banesco</option>
                      <option value="0137">Banco Sofitasa</option>
                      <option value="0138">Banco Plaza</option>
                      <option value="0151">BFC Banco Fondo Común</option>
                      <option value="0156">100% Banco</option>
                      <option value="0157">Banco del Sur</option>
                      <option value="0163">Banco del Tesoro</option>
                      <option value="0166">Banco Agrícola</option>
                      <option value="0168">Bancrecer</option>
                      <option value="0169">Mi Banco</option>
                      <option value="0171">Banco Activo</option>
                      <option value="0172">Bancamiga</option>
                      <option value="0174">Banplus</option>
                      <option value="0175">Bicentenario</option>
                      <option value="0177">Banco de la Fuerza Armada</option>
                      <option value="0191">Banco Nacional de Crédito</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Teléfono Pago Móvil *</label>
                    <input
                      type="tel"
                      placeholder="0412-1234567"
                      value={pagoForm.telefono}
                      onChange={e => setPagoForm(p => ({...p, telefono: e.target.value}))}
                      data-testid="pago-telefono"
                    />
                  </div>

                  <div className="form-field">
                    <label>Cédula *</label>
                    <input
                      type="text"
                      placeholder="V-12345678"
                      value={pagoForm.cedula}
                      onChange={e => setPagoForm(p => ({...p, cedula: e.target.value}))}
                      data-testid="pago-cedula"
                    />
                  </div>

                  <div className="form-field">
                    <label>Nombre del titular *</label>
                    <input
                      type="text"
                      placeholder="Nombre completo"
                      value={pagoForm.nombre_titular}
                      onChange={e => setPagoForm(p => ({...p, nombre_titular: e.target.value}))}
                      data-testid="pago-nombre"
                    />
                  </div>
                </>
              )}

              {/* ---------- BANCOLOMBIA ---------- */}
              {pagoForm.withdrawal_method === 'bancolombia' && (
                <>
                  <div className="form-field">
                    <label>Nombre del titular *</label>
                    <input
                      type="text"
                      placeholder="Nombre completo"
                      value={pagoForm.nombre_titular}
                      onChange={e => setPagoForm(p => ({...p, nombre_titular: e.target.value}))}
                      data-testid="bancolombia-nombre"
                    />
                  </div>

                  <div className="form-field">
                    <label>Número de cuenta *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Ej: 12345678901"
                      value={pagoForm.numero_cuenta}
                      onChange={e => setPagoForm(p => ({...p, numero_cuenta: e.target.value}))}
                      data-testid="bancolombia-cuenta"
                    />
                  </div>

                  <div className="form-field">
                    <label>Tipo de cuenta *</label>
                    <select
                      value={pagoForm.tipo_cuenta}
                      onChange={e => setPagoForm(p => ({...p, tipo_cuenta: e.target.value}))}
                      data-testid="bancolombia-tipo-cuenta"
                    >
                      <option value="ahorros">Ahorros</option>
                      <option value="corriente">Corriente</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Cédula *</label>
                    <input
                      type="text"
                      placeholder="Cédula del titular"
                      value={pagoForm.cedula}
                      onChange={e => setPagoForm(p => ({...p, cedula: e.target.value}))}
                      data-testid="bancolombia-cedula"
                    />
                  </div>
                </>
              )}

              {/* ---------- NEQUI ---------- */}
              {pagoForm.withdrawal_method === 'nequi' && (
                <>
                  <div className="form-field">
                    <label>Nombre del titular *</label>
                    <input
                      type="text"
                      placeholder="Nombre completo"
                      value={pagoForm.nombre_titular}
                      onChange={e => setPagoForm(p => ({...p, nombre_titular: e.target.value}))}
                      data-testid="nequi-nombre"
                    />
                  </div>

                  <div className="form-field">
                    <label>Número Nequi *</label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      placeholder="Ej: 3001234567"
                      value={pagoForm.numero_nequi}
                      onChange={e => setPagoForm(p => ({...p, numero_nequi: e.target.value}))}
                      data-testid="nequi-numero"
                    />
                  </div>

                  <div className="form-field">
                    <label>Cédula *</label>
                    <input
                      type="text"
                      placeholder="Cédula del titular"
                      value={pagoForm.cedula}
                      onChange={e => setPagoForm(p => ({...p, cedula: e.target.value}))}
                      data-testid="nequi-cedula"
                    />
                  </div>
                </>
              )}

              {/* Campo común: sugerencia de premio */}
              <div className="form-field">
                <label>🎁 ¿Qué premio te gustaría ganar próximamente?</label>
                <p className="form-field-hint">Tu idea podría aparecer en la ruleta 👀</p>
                <input
                  type="text"
                  placeholder="Escribe tu idea aquí..."
                  value={pagoForm.nota}
                  onChange={e => setPagoForm(p => ({...p, nota: e.target.value}))}
                  data-testid="pago-nota"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => setShowPagoMovilForm(false)}
                disabled={isClaiming}
              >
                Cancelar
              </button>
              <button 
                className="btn-confirm"
                onClick={submitPagoMovil}
                disabled={isClaiming}
                data-testid="pago-submit"
              >
                {isClaiming ? 'Enviando...' : 'Enviar solicitud'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.type}${toast.visualKey ? ' toast-visual toast-' + toast.visualKey : ''}`} data-testid={toast.visualKey ? `claim-toast-${toast.visualKey}` : 'claim-toast'}>
          {toast.visualKey && toast.visualKey.startsWith('xp_') ? (
            <div className="toast-xp-reward">
              <span className="toast-xp-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l2.9 6 6.6.6-5 4.6 1.5 6.6L12 17l-6 3.4 1.5-6.6-5-4.6 6.6-.6L12 2z"/>
                </svg>
              </span>
              <div className="toast-xp-body">
                <div className="toast-xp-amount">+{toast.xpAdded ?? 0} XP</div>
                <div className="toast-xp-sub">Premio entregado</div>
              </div>
              <span className="toast-xp-sparkle s1" aria-hidden="true"></span>
              <span className="toast-xp-sparkle s2" aria-hidden="true"></span>
              <span className="toast-xp-sparkle s3" aria-hidden="true"></span>
              <span className="toast-xp-sparkle s4" aria-hidden="true"></span>
            </div>
          ) : (
            toast.message
          )}
        </div>
      )}
    </div>
  );
};

export default MisPremios;
